import React, { useState, useEffect } from 'react';

/**
 * UserAvatar — a single, cache-busting avatar bubble used everywhere
 * a team member's name shows up (ticket assignees, referral audit
 * trail, activity feed, comment threads, etc.).
 *
 * The photo streams from `GET /api/team/members/{user_id}/photo`
 * (public endpoint — no auth token required) so we can drop it into
 * any UI without threading the auth header through.
 * On 404 / no user_id / no name → falls back to a coloured initials
 * bubble using the same slate/navy palette as the rest of the app.
 *
 * Props
 *   • userId   – user_id or agent user_id (optional; without it we
 *                skip the img and show initials only)
 *   • name     – full name for initials + accessible alt text
 *   • email    – optional, used as a last-resort initial source
 *   • size     – tailwind size class OR one of "xs" | "sm" | "md" | "lg"
 *   • ring     – true to add a subtle 2px ring (nice for lists)
 *   • className – extra wrapper classes if you need to tweak layout
 */
const SIZE_CLASSES = {
  xs: 'w-5 h-5 text-[9px]',
  sm: 'w-6 h-6 text-[10px]',
  md: 'w-8 h-8 text-xs',
  lg: 'w-10 h-10 text-sm',
  xl: 'w-14 h-14 text-base',
};

const PALETTE = [
  'bg-slate-700', 'bg-navy-800', 'bg-emerald-700', 'bg-amber-700',
  'bg-rose-700', 'bg-violet-700', 'bg-sky-700', 'bg-teal-700',
];

function _initials(name, email) {
  const src = (name || email || '?').trim();
  if (!src || src === '?') return '?';
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

function _tone(seed) {
  if (!seed) return PALETTE[0];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = ((hash << 5) - hash) + seed.charCodeAt(i);
  return PALETTE[Math.abs(hash) % PALETTE.length];
}

export function UserAvatar({
  userId, name, email, photoUrl, size = 'sm', ring = false, className = '', testId,
}) {
  const backend = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  const [broken, setBroken] = useState(false);

  // Reset the broken flag if the underlying user changes so a new
  // assignee doesn't inherit the previous person's broken state.
  useEffect(() => {
    setBroken(false);
  }, [userId, photoUrl]);

  const sizeCls = SIZE_CLASSES[size] || size;
  const ringCls = ring ? 'ring-2 ring-white shadow-sm' : '';
  const initials = _initials(name, email);

  // Resolve the source URL. Explicit `photoUrl` wins (used by the
  // referral leaderboard where the row already carries an absolute or
  // portal-relative photo path). Otherwise we hit the team-members
  // stream endpoint by user_id — which requires auth for now, so <img>
  // will 404 without a bearer header. That's fine: onError falls back
  // to the initials chip, so a broken URL never shows a broken-image
  // icon.
  let src = null;
  if (!broken) {
    if (photoUrl) {
      src = photoUrl.startsWith('http') ? photoUrl : `${backend}${photoUrl}`;
    } else if (userId) {
      src = `${backend}/api/team/members/${userId}/photo`;
    }
  }

  return (
    <span
      className={`inline-flex items-center justify-center rounded-full overflow-hidden font-semibold text-white ${_tone(userId || name || email)} ${sizeCls} ${ringCls} ${className}`}
      title={name || email || undefined}
      data-testid={testId}
    >
      {src ? (
        <img
          src={src}
          alt={name || email || 'User'}
          className="w-full h-full object-cover"
          onError={() => setBroken(true)}
        />
      ) : (
        <span aria-hidden>{initials}</span>
      )}
    </span>
  );
}

export default UserAvatar;
